import { masterLookImg, masterLookSprite } from './masterLook';
import { GroundClean } from './truepx/clean';
import { truePxScene } from './truepx/flags';
import { truePxImg, truePxSprite } from './truepx/page';
import {
  hash2,
  isBorder,
  mulberry32,
  nearLair,
  nearPlaza,
  onRoad,
  PLAZA_RADIUS,
  ROCK_HEIGHTS,
  LAIR_RADIUS,
  Surface,
  Tile,
  tileAt,
  toScreen,
  toWorld,
  TILE_H,
  TILE_W,
  valueNoise,
  HUB_WALL_VARIANT,
  PAVED_UNDER,
  TREE_SPECIES,
  type GameMap,
  type IsoOrigin,
  type Structure,
} from '@nine/shared';
import { box, canvas, ctx2d, ell, outline, finishArt, touchCanvas } from './pixel';
import {
  countryPixel,
  dikePixel,
  paddyPixel,
  plazaPixel,
  roadPixel,
  seaPixel,
  type FieldView,
} from './groundArt';
import { snowify, snowTreeSprites, structureSprite, treeKindAt, treeSprites, type Sprite } from './scenery';
import { structureLook } from './layoutLook';
import type { Detail } from './detail';
import { type GroundRemote, openGround } from './groundWorkers';
import { pavingRGB, TERRACE_STYLES } from './terraces';
import { gpuPrepare } from './gpuPrepare';
import { classicGroundPixel, classicPlains } from './classicPlains';
import { faceRec } from './buildings';
import { kitDraws } from './kit';
import { paintedFaces, TURNED_KINDS, turnedBlock, type TurnedSource } from './turned/kinds';

export interface Prop {
  img: HTMLCanvasElement;
  /** Top-left in world pixels. */
  x: number;
  y: number;
  /** Depth key comparable with entity x + y. */
  d: number;
  /** What it throws on the ground in the sun (render/lighting.ts), on the one prop that stands for it. */
  cast?: Caster;
  /**
   * The ground point it stands on (world pixels), where the camera turn (render/camera.ts) moves it
   * to; the middle of its bottom edge if not given. A building's strips share its footprint's middle,
   * so the building moves as one.
   */
  foot?: readonly [number, number];
  /** C0-LOOK P5: a stone block standing on the ground: it gets a contact-shadow ring (passes/contactAO.ts). */
  ao?: true;
  /**
   * C0-ROTATE-PORT: while the camera is turned or tilted, drawn by render/turned/scenery.ts from its painted view at
   * the camera's angle instead; kept here for what it throws (shadows, contact rings) and for the painted angle.
   */
  turned?: TurnedSource;
}

/**
 * Something tall that throws a sun shadow: a tree standing at (`x`, `y`) (its foot, world pixels),
 * `h` tall with a crown `half` wide each side; or a building whose front-left wall starts at its
 * west corner (`x`, `y`) and runs `w` tiles, `h` pixels tall.
 */
export type Caster =
  | { kind: 'tree'; x: number; y: number; h: number; half: number }
  | { kind: 'block'; x: number; y: number; w: number; h: number };

/** Structures that throw a block of shadow (gates, moon gates and boats stand too thin or on water). */
const CASTS = new Set<string>(['house', 'inn', 'pagoda', 'pavilion']);

export function mapOrigin(m: GameMap): IsoOrigin {
  return { ox: m.h * 16, oy: 32 };
}

type RGB = [number, number, number];

/** Isometric stone block, `h` pixels tall, drawn per pixel for a mottled texture. */
/**
 * `cap`: what tops a mountainside block — forest, bare crag or snow. Those are drawn without
 * outlines or bright top edges, so blocks of one height join into a single slope.
 */
export function rock(
  h: number,
  seed: number,
  wall: boolean,
  cap: 'stone' | 'forest' | 'crag' | 'snow' = 'stone',
): HTMLCanvasElement {
  const mountain = cap !== 'stone';
  const W = 32;
  const H = 16 + h;
  const c = canvas(W, H);
  const g = ctx2d(c);
  const img = g.createImageData(W, H);
  const s = img.data;
  const TOP: RGB = wall ? [132, 124, 114] : [158, 150, 137];
  // The key light is up and to the right: the front-right face in sun, the front-left in shade.
  const LEFT: RGB = wall ? [80, 74, 68] : [97, 90, 82];
  const RIGHT: RGB = wall ? [100, 93, 85] : [121, 113, 103];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const px = x + 0.5;
      const py = y + 0.5;
      let col: RGB | null = null;
      let edge = false;
      const td = Math.abs(px - 16) / 16 + Math.abs(py - 8) / 8;
      if (td <= 1) {
        col = TOP;
        edge = !mountain && td > 0.88;
      } else if (px < 16) {
        const y1 = 8 + px * 0.5;
        if (py >= y1 && py <= y1 + h) col = LEFT;
      } else {
        const y1 = 16 - (px - 16) * 0.5;
        if (py >= y1 && py <= y1 + h) col = RIGHT;
      }
      if (!col) continue;
      const n = valueNoise((x + seed * 31) / 4, (y + seed * 17) / 3);
      let m = n < 0.3 ? 1.1 : n > 0.68 ? 0.8 : 1;
      m += (hash2(x + seed * 101, y) - 0.5) * 0.08;
      if (edge) m *= 1.12;
      if (col !== TOP && (x === 15 || x === 16)) m *= 0.85;
      let [r, gg, b] = [col[0] * m, col[1] * m, col[2] * m];
      if (col === TOP && !wall && n > 0.74) [r, gg, b] = [92, 118, 78];
      // Mountainsides: forest over the grey crags lower down, bare rock higher, snow on the peaks.
      if (col === TOP && cap === 'forest')
        [r, gg, b] = n > 0.6 ? [58, 92, 58] : n < 0.3 ? [86, 120, 70] : [72, 106, 64];
      else if (col === TOP && cap === 'crag') [r, gg, b] = n > 0.55 ? [98, 114, 86] : [138, 134, 122];
      else if (col === TOP && cap === 'snow') [r, gg, b] = n > 0.7 ? [198, 206, 216] : [234, 240, 246];
      const k = (y * W + x) * 4;
      s[k] = r;
      s[k + 1] = gg;
      s[k + 2] = b;
      s[k + 3] = 255;
    }
  const rr = mulberry32(seed * 7 + 3);
  for (let k = 0; k < 3; k++) {
    let x = 2 + Math.floor(rr() * 28);
    let y = Math.floor(16 + rr() * Math.max(1, h - 4));
    for (let t = 0; t < 5; t++) {
      if (x >= 0 && x < W && y < H) {
        const q = (y * W + x) * 4;
        if (s[q + 3]) {
          s[q] = s[q]! * 0.55;
          s[q + 1] = s[q + 1]! * 0.55;
          s[q + 2] = s[q + 2]! * 0.55;
        }
      }
      x += rr() < 0.5 ? 0 : rr() < 0.5 ? -1 : 1;
      y++;
    }
  }
  g.putImageData(img, 0, 0);
  return mountain ? c : outline(c, '#28221d');
}

/** Bare dead tree for the blood lair. */
export function deadTree(): HTMLCanvasElement {
  const c = canvas(40, 70);
  const g = ctx2d(c);
  box(g, 18, 26, 4, 42, '#3a2c2a');
  for (const [x0, y0, x1, y1] of [
    [20, 40, 6, 20],
    [20, 34, 34, 14],
    [20, 28, 22, 6],
    [10, 26, 4, 22],
    [30, 20, 36, 22],
  ] as const) {
    const n = 14;
    for (let k = 0; k <= n; k++)
      box(g, Math.round(x0 + ((x1 - x0) * k) / n), Math.round(y0 + ((y1 - y0) * k) / n), 2, 1, '#3a2c2a');
  }
  return outline(c, '#140c0c');
}

/** Low decorations scattered on open ground (walkable, drawn in depth order). */
export function bush(seed: number): HTMLCanvasElement {
  const c = canvas(24, 16);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  ell(g, 12, 10, 10, 5, '#2f5e3a');
  ell(g, 9, 8, 6, 4, '#3f7a48');
  ell(g, 15, 7, 5, 4, '#4a8a52');
  for (let k = 0; k < 6; k++) box(g, 4 + Math.floor(r() * 16), 4 + Math.floor(r() * 8), 1, 1, '#6aa86a');
  if (seed % 2)
    for (let k = 0; k < 4; k++) box(g, 5 + Math.floor(r() * 14), 5 + Math.floor(r() * 6), 1, 1, '#e86a6a');
  return outline(c, '#16241a');
}

export function flowers(seed: number): HTMLCanvasElement {
  const c = canvas(18, 10);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  const cols = [
    ['#f2d24a', '#fff2a0'],
    ['#e86aa8', '#ffd0e8'],
    ['#ffffff', '#f2f2ff'],
    ['#8a6ae8', '#d0c0ff'],
  ][seed % 4]!;
  for (let k = 0; k < 7; k++) {
    const x = 1 + Math.floor(r() * 15);
    const y = 3 + Math.floor(r() * 6);
    box(g, x, y, 1, 2, '#4a7a3a');
    box(g, x - 1, y - 1, 3, 1, cols[0]);
    box(g, x, y - 1, 1, 1, cols[1]);
  }
  return c;
}

export function tallGrass(seed: number): HTMLCanvasElement {
  const c = canvas(20, 16);
  const g = ctx2d(c);
  const r = mulberry32(seed);
  for (let k = 0; k < 12; k++) {
    const x = 1 + Math.floor(r() * 18);
    const hgt = 6 + Math.floor(r() * 9);
    const lean = r() < 0.5 ? -1 : 1;
    for (let y = 0; y < hgt; y++)
      box(g, x + Math.round((y / hgt) * lean * 2), 15 - y, 1, 1, y > hgt * 0.6 ? '#a8c870' : '#6a9a4a');
  }
  return c;
}

/** Reeds and cattails at the water's edge: tall blades, a few brown seed heads. */
export function reeds(seed: number): HTMLCanvasElement {
  const c = canvas(22, 30);
  const g = ctx2d(c);
  const r = mulberry32(seed * 17 + 5);
  for (let k = 0; k < 11; k++) {
    const x = 3 + Math.floor(r() * 16);
    const hgt = 12 + Math.floor(r() * 16);
    const lean = (r() - 0.5) * 6;
    for (let y = 0; y < hgt; y++) {
      const px = x + Math.round((y / hgt) ** 2 * lean);
      box(g, px, 29 - y, 1, 1, y > hgt * 0.7 ? '#a6c46a' : y > hgt * 0.3 ? '#6e9a44' : '#4c7434');
    }
    if (k % 3 === 0) {
      const tx = x + Math.round(lean);
      box(g, tx, 29 - hgt - 4, 2, 5, '#6a3e24');
      box(g, tx + 1, 29 - hgt - 4, 1, 4, '#8a5634');
      box(g, tx, 29 - hgt - 6, 1, 2, '#6e9a44');
    }
  }
  return c;
}

/** Lotus pads floating on still water, one with a pink bloom. */
export function lilyPads(seed: number): HTMLCanvasElement {
  const c = canvas(28, 14);
  const g = ctx2d(c);
  const r = mulberry32(seed * 23 + 9);
  for (let k = 0; k < 3; k++) {
    const x = 5 + Math.floor(r() * 18);
    const y = 4 + Math.floor(r() * 7);
    const rx = 3 + Math.floor(r() * 3);
    ell(g, x, y + 1, rx, Math.ceil(rx / 2), '#2e5a3a');
    ell(g, x, y, rx, Math.ceil(rx / 2), '#4f8a46');
    box(g, x + 1, y - 1, rx - 1, 1, '#76ae5c');
    box(g, x, y, rx, 1, '#2e5a3a'); // the notch
  }
  if (seed % 2 === 0) {
    const [x, y] = [10 + Math.floor(r() * 8), 5];
    box(g, x - 2, y, 5, 2, '#e88aaa');
    box(g, x - 1, y - 2, 3, 2, '#f6c2d4');
    box(g, x, y - 3, 1, 1, '#ffffff');
    box(g, x, y, 1, 1, '#f2d24a');
  }
  return outline(c, '#1a3022');
}

/** A spray of fern fronds in the shade. */
export function fern(seed: number): HTMLCanvasElement {
  const c = canvas(22, 14);
  const g = ctx2d(c);
  const r = mulberry32(seed * 7 + 2);
  for (let k = 0; k < 6; k++) {
    const dir = k % 2 ? 1 : -1;
    const len = 6 + Math.floor(r() * 4);
    const rise = 4 + Math.floor(r() * 6);
    for (let q = 0; q <= len; q++) {
      const x = 11 + dir * q;
      const y = 13 - Math.round(rise * Math.sin((q / len) * Math.PI * 0.8));
      box(g, x, y, 1, 1, '#3e6e34');
      if (q % 2 === 0 && q < len) box(g, x, y - 1, 1, 1, q > len / 2 ? '#86b85a' : '#5a9440');
    }
  }
  return outline(c, '#16261a');
}

/** Mushrooms in a ring at the foot of the trees. */
export function mushrooms(seed: number): HTMLCanvasElement {
  const c = canvas(14, 10);
  const g = ctx2d(c);
  const r = mulberry32(seed * 3 + 1);
  const red = seed % 2 === 0;
  for (let k = 0; k < 3; k++) {
    const x = 2 + Math.floor(r() * 9);
    const y = 4 + Math.floor(r() * 4);
    box(g, x, y, 1, 3, '#e8dcc0');
    box(g, x - 1, y - 1, 3, 1, red ? '#c0392b' : '#b08a5a');
    box(g, x - 2, y, 5, 1, red ? '#a02a20' : '#8a6a44');
    if (red) box(g, x, y - 1, 1, 1, '#ffffff');
  }
  return outline(c, '#20160e');
}

/** Small weathered stones half sunk in the grass. */
export function stones(seed: number): HTMLCanvasElement {
  const c = canvas(18, 10);
  const g = ctx2d(c);
  const r = mulberry32(seed * 5 + 7);
  for (let k = 0; k < 2 + (seed % 2); k++) {
    const x = 4 + Math.floor(r() * 10);
    const y = 5 + Math.floor(r() * 3);
    const rx = 2 + Math.floor(r() * 3);
    ell(g, x, y, rx, Math.ceil(rx * 0.6), '#7a7468');
    box(g, x, y - 1, rx, 1, '#a49c8c');
    box(g, x - rx + 1, y + Math.ceil(rx * 0.6) - 1, rx, 1, '#5a564e');
    if (r() < 0.6) box(g, x + 1, y - 1, 2, 1, '#6a8a4a'); // moss
  }
  return outline(c, '#24201c');
}

/**
 * Where the stone lanterns ringing a plaza centred at (`x`, `y`) stand (map cells, as turned for
 * the view): eight round it, lit at night (render/lighting.ts).
 */
export function plazaLanternRing(x: number, y: number): [number, number][] {
  return Array.from({ length: 8 }, (_, k) => {
    const a = (k / 8) * Math.PI * 2 + Math.PI / 8;
    return [x + Math.cos(a) * 5.6, y + Math.sin(a) * 5.6];
  });
}

/** Stone lantern (tōrō-like) that lines the plaza, with a warm glow. */
export function stoneLantern(): HTMLCanvasElement {
  const c = canvas(16, 30);
  const g = ctx2d(c);
  box(g, 3, 26, 10, 3, '#7a7268');
  box(g, 6, 16, 4, 10, '#8a8275');
  box(g, 3, 11, 10, 6, '#8a8275');
  box(g, 5, 12, 6, 4, '#ffcf6a');
  box(g, 6, 13, 4, 2, '#fff2c0');
  box(g, 1, 8, 14, 3, '#6d655c');
  box(g, 4, 5, 8, 3, '#6d655c');
  box(g, 7, 2, 2, 3, '#6d655c');
  return outline(c, '#24201c');
}

/** Banner on a pole, cloth swaying, with a stitched emblem. */
export function banner(cloth: string, mark: string): HTMLCanvasElement {
  const c = canvas(18, 64);
  const g = ctx2d(c);
  box(g, 3, 2, 2, 60, '#5a3a24');
  box(g, 2, 1, 4, 2, '#e8b84a');
  box(g, 5, 6, 10, 26, cloth);
  box(g, 5, 30, 4, 4, cloth);
  box(g, 11, 30, 4, 6, cloth);
  box(g, 7, 10, 6, 1, mark);
  box(g, 9, 11, 2, 12, mark);
  box(g, 7, 16, 6, 1, mark);
  box(g, 7, 22, 6, 1, mark);
  box(g, 5, 6, 10, 1, '#e8b84a');
  return outline(c, '#1c1410');
}

/** A clump of rice shoots standing in a paddy. */
export function riceTuft(seed: number): HTMLCanvasElement {
  const c = canvas(14, 12);
  const g = ctx2d(c);
  const r = mulberry32(seed * 31);
  for (let k = 0; k < 7; k++) {
    const x = 3 + Math.floor(r() * 8);
    const hgt = 5 + Math.floor(r() * 6);
    const lean = r() < 0.5 ? -1 : 1;
    for (let y = 0; y < hgt; y++)
      box(g, x + Math.round((y / hgt) * lean * 2), 11 - y, 1, 1, y > hgt * 0.55 ? '#9ccc5a' : '#5e9a3a');
  }
  return c;
}

/** A tarred post standing up out of the water at the pier's edge. */
export function pierPost(): HTMLCanvasElement {
  const c = canvas(6, 16);
  const g = ctx2d(c);
  box(g, 1, 0, 4, 16, '#4a3424');
  box(g, 3, 0, 2, 16, '#6a4a32');
  box(g, 1, 0, 4, 1, '#8a6a44');
  return outline(c, '#1c140e');
}

export function stele(): HTMLCanvasElement {
  const c = canvas(26, 48);
  const g = ctx2d(c);
  const rr = mulberry32(77);
  box(g, 1, 40, 24, 7, '#6d655c');
  box(g, 1, 40, 24, 1, '#958c80');
  ell(g, 13, 8, 7, 4, '#8a8275');
  box(g, 6, 8, 14, 33, '#8a8275');
  box(g, 6, 8, 3, 33, '#6f685e');
  box(g, 17, 8, 3, 33, '#9d9588');
  for (const x of [10, 13, 16])
    for (let y = 12; y < 36; y += 2) if (rr() < 0.75) box(g, x, y, rr() < 0.5 ? 1 : 2, 1, '#4c463f');
  box(g, 15, 35, 3, 3, '#a8322c');
  return outline(c, '#24201c');
}

export function groundSword(): HTMLCanvasElement {
  const c = canvas(9, 22);
  const g = ctx2d(c);
  box(g, 4, 8, 1, 14, '#9aaac2');
  box(g, 5, 8, 1, 14, '#dfe7f2');
  box(g, 2, 7, 6, 1, '#c9a75a');
  box(g, 4, 2, 2, 5, '#3a2a3a');
  box(g, 4, 1, 2, 1, '#c9a75a');
  box(g, 6, 3, 1, 4, '#c0392b');
  box(g, 7, 6, 1, 2, '#c0392b');
  return outline(c);
}

/** Side of the square chunks the ground is painted in, so even a big map never needs one huge canvas. */
export const GROUND_CHUNK = 512;
/** Chunks kept painted (the least recently drawn go first): about 50 MB at most. */
const GROUND_KEEP = 48;
/** Milliseconds a frame spends painting ground ahead of the camera. */
const GROUND_BUDGET_MS = 3;
/** How far beyond the view (ground pixels) chunks are painted before they come into it. */
const GROUND_AHEAD = 320;
/**
 * Canvas pixels per ground pixel the ground workers paint at: 1, whole art pixels, the same grid
 * the buildings are drawn on, so ground and structures read as one pixel grid.
 */
const GROUND_RES = 1;
/** Chunks kept with ground workers (each a 512² picture, about 1 MB). */
const GROUND_KEEP_REMOTE = GROUND_KEEP;
/**
 * With ground workers: how far beyond the view chunks are asked for (lane C0-P1: twice the painted look-ahead, so on
 * a big map a walking player meets chunks the workers have already painted), and how many coarse stand-ins the main
 * thread paints in one frame (the rest show a plain field colour for the frame or two until theirs come).
 */
const GROUND_AHEAD_REMOTE = 640;
const COARSE_PER_FRAME = 1;
let plainField: HTMLCanvasElement | null = null;
/** One flat chunk of the field's colour, standing in for ground not painted yet. */
function fieldStandIn(): HTMLCanvasElement {
  if (plainField) return plainField;
  const c = canvas(GROUND_CHUNK, GROUND_CHUNK);
  const g = ctx2d(c);
  g.fillStyle = '#7d8a52';
  g.fillRect(0, 0, GROUND_CHUNK, GROUND_CHUNK);
  return (plainField = c);
}

/** A speck of decoration on the ground (whole-ground pixels): x, y, width, height, colour. */
export type Speck = readonly [number, number, number, number, string];

/** Value noise that repeats every `p` cells, so a pattern built from it tiles without a seam. */
function loopNoise(x: number, y: number, p: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const u = (x - xi) * (x - xi) * (3 - 2 * (x - xi));
  const v = (y - yi) * (y - yi) * (3 - 2 * (y - yi));
  const h = (a: number, b: number) => hash2(((a % p) + p) % p, ((b % p) + p) % p);
  const top = h(xi, yi) + (h(xi + 1, yi) - h(xi, yi)) * u;
  const bot = h(xi, yi + 1) + (h(xi + 1, yi + 1) - h(xi, yi + 1)) * u;
  return top + (bot - top) * v;
}

let clouds: HTMLCanvasElement | null = null;
/** A tile of cloud shadow (opaque where shaded), squashed to lie on the iso ground. */
function cloudShadows(): HTMLCanvasElement {
  if (clouds) return clouds;
  const [W, H] = [640, 320];
  const c = canvas(W, H);
  const g = ctx2d(c);
  const img = g.createImageData(W, H);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const n = loopNoise(x / 80, y / 40, 8) * 0.7 + loopNoise(x / 32, y / 16, 20) * 0.3;
      // Solid in the middle, dithered at the rim.
      const edge = (n - 0.6) / 0.05;
      if (edge < (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16) continue;
      const k = (y * W + x) * 4;
      img.data[k] = 18;
      img.data[k + 1] = 26;
      img.data[k + 2] = 58;
      img.data[k + 3] = 255;
    }
  g.putImageData(img, 0, 0);
  return (clouds = c);
}

/**
 * The pixel-level ground — checkered stone with mottled patches, bevelled seams, roads and the
 * blood lair — painted chunk by chunk as the camera comes near, and drawn chunk by chunk.
 */
export class Ground {
  readonly width: number;
  readonly height: number;
  private readonly chunks = new Map<number, GroundJob>();
  /**
   * With ground workers: each chunk's picture as it stands — a quick coarse one painted here
   * until the worker's full-resolution one arrives — and whether the worker has been asked.
   */
  private readonly tiles = new Map<
    number,
    { img: CanvasImageSource | null; fine: boolean; asked: boolean }
  >();
  private remote: GroundRemote | null;
  private readonly specks: Speck[];
  private readonly near: Shore;
  /** Points on the water (ground pixels) where sunlight glints, each with its own phase. */
  private readonly glints: [number, number, number][] = [];
  /** Points along the beach (ground pixels), the way out to sea (pixels per tile) and a phase. */
  private readonly surf: [number, number, number, number, number][] = [];
  /** Open countryside per tile (no plaza, road or border), where the wind shimmers over the grass. */
  private readonly field: Uint8Array;

  constructor(
    private readonly m: GameMap,
    private readonly o: IsoOrigin,
    private readonly view: ViewMapping = SAME,
    /** The camera's map size and quarter turn, so workers can paint the same ground. */
    private readonly at?: { n: number; rot: number },
  ) {
    this.remote = at ? openGround(m, o, at, GROUND_RES) : null;
    this.width = (m.w + m.h) * 16;
    this.height = (m.w + m.h) * 8 + o.oy * 2;
    this.specks = groundSpecks(m, o);
    this.near = shoreDistances(m);
    if (m.biome !== 'snow')
      for (let j = 0; j < m.h; j++)
        for (let i = 0; i < m.w; i++) {
          if (m.tiles[j * m.w + i] !== Tile.Water) continue;
          for (let k = 0; k < 2; k++) {
            const h = hash2(i * 31 + k * 7, j * 17 + k);
            if (h > 0.4) continue;
            const [sx, sy] = toScreen(i + hash2(i, j + k * 3), j + hash2(j + k, i), o);
            this.glints.push([Math.round(sx), Math.round(sy), h * 40]);
          }
          // Each side the sea meets the beach, a run of points the waves roll in to.
          for (const [a, b] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
          ] as const) {
            const t = tileAt(m, i + a, j + b);
            if (t === Tile.Water || t === Tile.Pier || t === Tile.Rock || isBorder(m, i + a, j + b)) continue;
            const [nx, ny] = toScreen(-a, -b, { ox: 0, oy: 0 });
            for (let u = 0.1; u < 1; u += 0.2) {
              const [ex, ey] = [a ? i + (a > 0 ? 1 : 0) : i + u, b ? j + (b > 0 ? 1 : 0) : j + u];
              const [sx, sy] = toScreen(ex, ey, o);
              this.surf.push([sx, sy, nx, ny, hash2(Math.round(sx * 7), Math.round(sy * 3))]);
            }
          }
        }
    this.field = new Uint8Array(m.w * m.h);
    if (m.biome !== 'snow')
      for (let j = 0; j < m.h; j++)
        for (let i = 0; i < m.w; i++)
          this.field[j * m.w + i] = +(
            m.tiles[j * m.w + i] === Tile.Floor &&
            !isBorder(m, i, j) &&
            !nearPlaza(m, i + 0.5, j + 0.5, PLAZA_RADIUS) &&
            !onRoad(m, i + 0.5, j + 0.5)
          );
  }

  /**
   * The living layer over the painted ground: wind shimmering across the grass, waves rolling up
   * the beach, and cloud shadows drifting over everything.
   */
  drawAmbience(g: CanvasRenderingContext2D, ox: number, oy: number, vw: number, vh: number, t: number): void {
    this.drawShimmer(g, ox, oy, vw, vh, t);
    this.drawSurf(g, ox, oy, vw, vh, t);
    this.drawClouds(g, ox, oy, t);
  }

  /** Gusts running across the grass: bands of blades catching the light as the wind combs them. */
  private drawShimmer(
    g: CanvasRenderingContext2D,
    ox: number,
    oy: number,
    vw: number,
    vh: number,
    t: number,
  ): void {
    const { m, o } = this;
    const corners = [
      toWorld(-ox, -oy, o),
      toWorld(vw - ox, -oy, o),
      toWorld(-ox, vh - oy, o),
      toWorld(vw - ox, vh - oy, o),
    ];
    const i0 = Math.max(0, Math.floor(Math.min(...corners.map((c) => c[0]))));
    const i1 = Math.min(m.w - 1, Math.ceil(Math.max(...corners.map((c) => c[0]))));
    const j0 = Math.max(0, Math.floor(Math.min(...corners.map((c) => c[1]))));
    const j1 = Math.min(m.h - 1, Math.ceil(Math.max(...corners.map((c) => c[1]))));
    for (let j = j0; j <= j1; j++)
      for (let i = i0; i <= i1; i++) {
        if (!this.field[j * m.w + i]) continue;
        for (let k = 0; k < 6; k++) {
          // toScreen inlined (same arithmetic): no [x, y] pair per blade, thousands a frame (perf 2026-10-13).
          const gx = i + hash2(i * 7 + k, j * 3);
          const gy = j + hash2(j * 5 + k, i * 11);
          const sx = (gx - gy) * (TILE_W / 2) + o.ox;
          const sy = (gx + gy) * (TILE_H / 2) + o.oy;
          const x = Math.round(sx + ox);
          const y = Math.round(sy + oy);
          if (x < 0 || y < 2 || x >= vw || y >= vh) continue;
          // Bands sweep across the screen down-wind, bending as they go.
          const band = Math.sin((sx * 0.7 + sy * 1.3) / 46 - t * 1.9 + valueNoise(sx / 140, sy / 70) * 4);
          if (band < 0.8) continue;
          g.fillStyle = band > 0.94 ? 'rgba(236,240,168,.55)' : 'rgba(214,226,140,.32)';
          g.fillRect(x, y - 2, 1, 2);
        }
      }
  }

  /** Waves: a crest coming in over the shallows, then foam washing up the sand and sliding back. */
  private drawSurf(
    g: CanvasRenderingContext2D,
    ox: number,
    oy: number,
    vw: number,
    vh: number,
    t: number,
  ): void {
    // Indexed, not destructured (an iterator and a tuple per point per frame), and the fill set only when it
    // changes (each set is parsed again by the canvas).
    const surf = this.surf;
    let fill = '';
    for (let i = 0; i < surf.length; i++) {
      const p = surf[i]!;
      const h = p[4];
      if (h > 0.85) continue; // a gap in the foam
      const ex = p[0],
        ey = p[1];
      const x0 = ex + ox;
      const y0 = ey + oy;
      if (x0 < -40 || y0 < -24 || x0 > vw + 40 || y0 > vh + 24) continue;
      // Neighbouring points share their wave, so each crest comes in as one line.
      const s = (t * 0.28 + valueNoise(ex / 60, ey / 30) * 1.6 + h * 0.08) % 1;
      if (s < 0.78) {
        const k = s / 0.78;
        const out = (1 - k) * 0.9;
        g.globalAlpha = Math.sin(k * Math.PI) * 0.75;
        if (fill !== '#e6f2f2') g.fillStyle = fill = '#e6f2f2';
        g.fillRect(Math.round(x0 + p[2] * out), Math.round(y0 + p[3] * out), 2, 1);
      } else {
        const k = (s - 0.78) / 0.22;
        const up = Math.sin(k * Math.PI) * 0.3;
        g.globalAlpha = (1 - k) * 0.85;
        if (fill !== '#f2f8f6') g.fillStyle = fill = '#f2f8f6';
        g.fillRect(Math.round(x0 - p[2] * up), Math.round(y0 - p[3] * up), 3, 1);
      }
    }
    g.globalAlpha = 1;
  }

  /** Shadows of clouds drifting over the land and sea, dithered at the edges. */
  private drawClouds(g: CanvasRenderingContext2D, ox: number, oy: number, t: number): void {
    const { m, o } = this;
    const pat = g.createPattern(cloudShadows(), 'repeat');
    if (!pat) return;
    pat.setTransform(new DOMMatrix().translate(Math.round(ox + t * 7), Math.round(oy + t * 3.5)));
    g.save();
    g.beginPath();
    for (const [i, j] of [
      [0, 0],
      [m.w, 0],
      [m.w, m.h],
      [0, m.h],
    ] as const) {
      const [x, y] = toScreen(i, j, o);
      g.lineTo(x + ox, y + oy);
    }
    g.closePath();
    g.globalAlpha = 0.17;
    g.fillStyle = pat;
    g.fill();
    g.restore();
  }

  /** Sun glints winking on and off across the water. */
  drawGlints(g: CanvasRenderingContext2D, ox: number, oy: number, vw: number, vh: number, t: number): void {
    for (const [x, y, ph] of this.glints) {
      const sx = x + ox;
      const sy = y + oy;
      if (sx < -4 || sy < -4 || sx > vw + 4 || sy > vh + 4) continue;
      const a = Math.sin(t * 1.6 + ph);
      if (a < 0.55) continue;
      g.fillStyle = a > 0.9 ? 'rgba(255,255,255,.9)' : 'rgba(210,236,250,.6)';
      g.fillRect(sx, sy, a > 0.8 ? 3 : 2, 1);
      if (a > 0.95) g.fillRect(sx + 1, sy - 1, 1, 3);
    }
  }

  /**
   * Draws the chunks under a viewport `vw`×`vh` whose ground origin sits at (ox, oy), then paints
   * on for a few milliseconds: first the visible chunks still coarse, then the ring just outside
   * the view, so the ground is ready before the camera gets there.
   */
  draw(g: CanvasRenderingContext2D, ox: number, oy: number, vw: number, vh: number): void {
    if (this.truePx !== truePxScene()) this.repaint();
    if (this.remote && !this.remote.alive) this.remote = null;
    if (this.remote) return this.drawRemote(this.remote, g, ox, oy, vw, vh);
    const C = GROUND_CHUNK;
    const range = (o: number, v: number, size: number, pad: number) =>
      [
        Math.max(0, Math.floor((-o - pad) / C)),
        Math.min(Math.floor((size - 1) / C), Math.floor((v - o + pad) / C)),
      ] as const;
    const [cx0, cx1] = range(ox, vw, this.width, 0);
    const [cy0, cy1] = range(oy, vh, this.height, 0);
    const visible: GroundJob[] = [];
    for (let cy = cy0; cy <= cy1; cy++)
      for (let cx = cx0; cx <= cx1; cx++) {
        const job = this.job(cx, cy);
        // Never seen yet (a teleport, a zone just loaded): its coarse pass now, a sixteenth of the work.
        if (!job.shown) paintSome(job, Infinity, 1);
        g.drawImage(job.c, ox + cx * C, oy + cy * C);
        visible.push(job);
      }
    const until = performance.now() + GROUND_BUDGET_MS;
    for (const job of visible) if (job.pass < 2) paintSome(job, until);
    const [px0, px1] = range(ox, vw, this.width, GROUND_AHEAD);
    const [py0, py1] = range(oy, vh, this.height, GROUND_AHEAD);
    for (let cy = py0; cy <= py1 && performance.now() < until; cy++)
      for (let cx = px0; cx <= px1 && performance.now() < until; cx++) {
        const job = this.job(cx, cy);
        if (job.pass < 2) paintSome(job, until);
      }
    while (this.chunks.size > GROUND_KEEP) this.chunks.delete(this.chunks.keys().next().value!);
  }

  /**
   * Drawing with ground workers: chunks come back from them at full resolution; one with nothing
   * yet gets a quick coarse picture painted here meanwhile. The ring just outside the view is asked
   * for ahead of time.
   */
  private drawRemote(
    remote: GroundRemote,
    g: CanvasRenderingContext2D,
    ox: number,
    oy: number,
    vw: number,
    vh: number,
  ): void {
    const C = GROUND_CHUNK;
    const range = (o: number, v: number, size: number, pad: number) =>
      [
        Math.max(0, Math.floor((-o - pad) / C)),
        Math.min(Math.floor((size - 1) / C), Math.floor((v - o + pad) / C)),
      ] as const;
    const tile = (cx: number, cy: number, urgent: boolean) => {
      const key = cy * 4096 + cx;
      let t = this.tiles.get(key);
      if (t) this.tiles.delete(key);
      else t = { img: null, fine: false, asked: false };
      this.tiles.set(key, t);
      if (!t.asked) {
        t.asked = true;
        const [X0, Y0] = [cx * C, cy * C];
        const entry = t;
        remote.ask(
          key,
          X0,
          Y0,
          Math.min(C, this.width - X0),
          Math.min(C, this.height - Y0),
          urgent,
          (img, last) => {
            if (entry.fine) return img.close();
            if (entry.img && 'close' in entry.img) (entry.img as ImageBitmap).close();
            entry.img = img;
            entry.fine = last;
            // A big map (lane C0-P1): onto the GPU now, between frames, not on the frame it first shows.
            if (this.m.w * this.m.h > 128 * 128) gpuPrepare.fn?.(img);
          },
        );
      }
      return t;
    };
    const [cx0, cx1] = range(ox, vw, this.width, 0);
    const [cy0, cy1] = range(oy, vh, this.height, 0);
    // A big map (lane C0-P1) keeps the frame: few coarse pictures per frame, a plain field for the rest meanwhile.
    let coarse = this.m.w * this.m.h > 128 * 128 ? COARSE_PER_FRAME : Infinity;
    for (let cy = cy0; cy <= cy1; cy++)
      for (let cx = cx0; cx <= cx1; cx++) {
        const t = tile(cx, cy, true);
        const [X0, Y0] = [cx * C, cy * C];
        const [CW, CH] = [Math.min(C, this.width - X0), Math.min(C, this.height - Y0)];
        if (!t.img && coarse > 0) {
          // Nothing back yet (a teleport, a zone just loaded): a coarse picture now, a sixteenth of the work.
          coarse--;
          const job = groundJob(this.m, this.o, this.view, X0, Y0, CW, CH, this.specks, this.near);
          paintSome(job, Infinity, 1);
          t.img = job.c;
        }
        g.drawImage(t.img ?? fieldStandIn(), ox + X0, oy + Y0, CW, CH);
      }
    const ahead = this.m.w * this.m.h > 128 * 128 ? GROUND_AHEAD_REMOTE : GROUND_AHEAD;
    const [px0, px1] = range(ox, vw, this.width, ahead);
    const [py0, py1] = range(oy, vh, this.height, ahead);
    for (let cy = py0; cy <= py1; cy++) for (let cx = px0; cx <= px1; cx++) tile(cx, cy, false);
    while (this.tiles.size > GROUND_KEEP_REMOTE) {
      const [key, t] = this.tiles.entries().next().value!;
      if (t.img && 'close' in t.img) (t.img as ImageBitmap).close();
      remote.forget(key);
      this.tiles.delete(key);
    }
  }

  /** Painted with the true-pixel clean-up (render/truepx/); the ground repaints when the setting changes. */
  private truePx = truePxScene();
  private repaint(): void {
    this.close();
    this.chunks.clear();
    this.truePx = truePxScene();
    this.remote = this.at ? openGround(this.m, this.o, this.at, GROUND_RES) : null;
  }

  /** Lets go of the workers' copy of this ground (the camera turned or the zone changed). */
  close(): void {
    this.remote?.close();
    for (const t of this.tiles.values()) if (t.img && 'close' in t.img) (t.img as ImageBitmap).close();
    this.tiles.clear();
  }

  /** The chunk at (cx, cy), started if new, and moved to the back of the forget queue. */
  private job(cx: number, cy: number): GroundJob {
    const C = GROUND_CHUNK;
    const key = cy * 4096 + cx;
    let job = this.chunks.get(key);
    if (job) this.chunks.delete(key);
    else {
      const [X0, Y0] = [cx * C, cy * C];
      job = groundJob(
        this.m,
        this.o,
        this.view,
        X0,
        Y0,
        Math.min(C, this.width - X0),
        Math.min(C, this.height - Y0),
        this.specks,
        this.near,
      );
    }
    this.chunks.set(key, job);
    return job;
  }
}

/** Height of the countryside's gentle swells (map coordinates), 0..1. */
const swellAt = (x: number, y: number): number =>
  valueNoise(x / 2.4 + 3, y / 2.4 + 1) * 0.7 + valueNoise(x / 0.9 + 5, y / 0.9) * 0.3;

/**
 * Sunlight on the swells at a view point: the sun stands up and to the right on screen (−y on the
 * view grid, a little −x), so slopes facing it are lit.
 */
function sunOn(view: ViewMapping, gx: number, gy: number): number {
  const e = 0.12;
  const [ax, ay] = view.unturn(gx - e / 3, gy - e);
  const [bx, by] = view.unturn(gx + e / 3, gy + e);
  return (swellAt(ax, ay) - swellAt(bx, by)) * 5.6;
}

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

/** The countryside as the field painter sees it: grass gathers in the hollows, the crests dry out. */
function fieldView(o: IsoOrigin, view: ViewMapping): FieldView {
  const [ax, ay] = view.unturn(0, 0);
  const [bx, by] = view.unturn(1, 0);
  const [cx, cy] = view.unturn(0, 1);
  return {
    grid: (sx, sy) => toWorld(sx, sy, o),
    unturn: (x, y) => view.unturn(x, y),
    axes: [
      [bx - ax, by - ay],
      [cx - ax, cy - ay],
    ],
    cover: (ux, uy) => {
      const b = biomeAt(ux, uy);
      const grass = Math.min(1, Math.max(0, (b - 0.42) / 0.2));
      const dirt = Math.min(1, Math.max(0, (0.3 - b) / 0.12));
      return Math.min(1, Math.max(0, (1 - dirt) * (0.55 + 0.45 * grass) + (0.5 - swellAt(ux, uy)) * 0.4));
    },
    lush: (ux, uy) => Math.min(1, Math.max(0, (biomeAt(ux, uy) - 0.42) / 0.2)),
    sun: (gx, gy) => sunOn(view, gx, gy),
  };
}

/** A per-tile distance blended between tile centres, so bands drawn from it run smooth. */
function blended(m: GameMap, d: Uint8Array, i: number, j: number, fx: number, fy: number): number {
  const at = (a: number, b: number) =>
    d[Math.min(m.h - 1, Math.max(0, b)) * m.w + Math.min(m.w - 1, Math.max(0, a))]!;
  const [ax, ay] = [fx - 0.5, fy - 0.5];
  const [i0, j0] = [ax < 0 ? i - 1 : i, ay < 0 ? j - 1 : j];
  const [tx, ty] = [ax < 0 ? ax + 1 : ax, ay < 0 ? ay + 1 : ay];
  const top = at(i0, j0) + (at(i0 + 1, j0) - at(i0, j0)) * tx;
  const bot = at(i0, j0 + 1) + (at(i0 + 1, j0 + 1) - at(i0, j0 + 1)) * tx;
  return top + (bot - top) * ty;
}

/**
 * A chunk of ground being painted, a little at a time so walking never waits on it: first a coarse
 * pass (one pixel in sixteen, filled out as 4×4 blocks) that can be shown at once, then every pixel.
 */
interface GroundJob {
  readonly c: HTMLCanvasElement;
  /** The pixels being painted; let go once the chunk is finished. */
  img: ImageData | null;
  readonly X0: number;
  readonly Y0: number;
  readonly CW: number;
  readonly CH: number;
  /** Canvas pixels per ground pixel (see `GROUND_RES`); 2 would paint it at double resolution. */
  readonly res: number;
  /** 0 the coarse pass, 1 every pixel, 2 finished. */
  pass: 0 | 1 | 2;
  /** The next row (in the chunk) of the pass under way. */
  row: number;
  /** The canvas has something on it (the coarse pass at least). */
  shown: boolean;
  /** Paints ground pixel (x, y) into `s` at byte `k`. */
  readonly pixel: (x: number, y: number, s: Uint8ClampedArray, k: number) => void;
  readonly specks: readonly Speck[];
  /** Lane C0-TRUEPX-GAME: the true-pixel clean-up of the finished pixels, while under way. */
  clean?: GroundClean | undefined;
}

/** Starts painting pixels [X0, X0+CW) × [Y0, Y0+CH) of the whole ground into a canvas of their own. */
function groundJob(
  m: GameMap,
  o: IsoOrigin,
  view: ViewMapping,
  X0: number,
  Y0: number,
  CW: number,
  CH: number,
  specks: readonly Speck[],
  near: Shore,
  res = 1,
): GroundJob {
  const c = canvas(CW * res, CH * res);
  const fv = fieldView(o, view);
  const pixel = (x: number, y: number, s: Uint8ClampedArray, k: number): void => {
    const [gx, gy] = toWorld(x + 0.5, y + 0.5, o);
    const i = Math.floor(gx);
    const j = Math.floor(gy);
    let r: number;
    let gg: number;
    let bb: number;
    if (i < 0 || j < 0 || i >= m.w || j >= m.h) {
      // Off the map: leave transparent so the sky and mountains show through.
      s[k + 3] = 0;
      return;
    } else {
      const fx = gx - i;
      const fy = gy - j;
      const pd = Math.hypot(gx - m.plaza.x, gy - m.plaza.y);
      const ld = Math.hypot(gx - m.lair.x, gy - m.lair.y);
      const t = m.tiles[j * m.w + i]!;
      const wet = waterLook(m, near, t, i, j, fx, fy, x, y);
      if (wet) {
        [r, gg, bb] = wet;
        if (m.tint) [r, gg, bb] = [r * m.tint[0], gg * m.tint[1], bb * m.tint[2]];
        s[k] = r;
        s[k + 1] = gg;
        s[k + 2] = bb;
        s[k + 3] = 255;
        return;
      }
      // Under a solid prop on paving (the 256 m plains: plainsMap.ts `PAVED_UNDER`), the paving goes on.
      const pavedUnder = t === Tile.Building && m.variant[j * m.w + i]! >= PAVED_UNDER;
      if (t === Tile.Paved || t === Tile.Terrace || pavedUnder) {
        // A temple courtyard's flagstones (and under a terrace), in its style's pattern.
        const sv = m.variant[j * m.w + i]! - (pavedUnder ? PAVED_UNDER : 0);
        const st = TERRACE_STYLES[sv % TERRACE_STYLES.length]!;
        [r, gg, bb] = pavingRGB(st, gx, gy, x, y);
        if (m.tint) [r, gg, bb] = [r * m.tint[0], gg * m.tint[1], bb * m.tint[2]];
        s[k] = r;
        s[k + 1] = gg;
        s[k + 2] = bb;
        s[k + 3] = 255;
        return;
      }
      if (classicPlains(m)) {
        // The plains hub painted as the 2026-10-04 client did (classicPlains.ts).
        const [ux, uy] = view.unturn(gx, gy);
        [r, gg, bb] = classicGroundPixel(m, x, y, gx, gy, biomeAt(ux, uy));
        s[k] = r;
        s[k + 1] = gg;
        s[k + 2] = bb;
        s[k + 3] = 255;
        return;
      }
      let base: RGB;
      const surf = m.surface?.[j * m.w + i];
      if (surf !== undefined && onRoad(m, gx, gy)) {
        // A road of the surface layer: ruts a little in from each verge, grass at its edge.
        const v = blended(m, near.toVerge, i, j, fx, fy);
        base = roadPixel(fv, x, y, gx, gy, Math.max(0, Math.min(1, 1.25 - v * 0.45)));
      } else if (surf === Surface.Sand || surf === Surface.Gravel || surf === Surface.Snow) {
        // Beaches, the sand bar, the training ground's packed sand, the quarry.
        base = countryPixel(fv, x, y, gx, gy, 0);
      } else if (surf !== undefined && surf !== Surface.Grass && surf !== Surface.Moss) {
        base = dikePixel(x, y, sunOn(view, gx, gy));
      } else if (pd < PLAZA_RADIUS && !m.surface) {
        base = plazaPixel(x, y, gx, gy, gx - m.plaza.x, gy - m.plaza.y, PLAZA_RADIUS);
      } else if (!m.surface && onRoad(m, gx, gy)) {
        base = roadPixel(fv, x, y, gx, gy, Math.min(Math.abs(gx - m.plaza.x), Math.abs(gy - m.plaza.y)));
      } else if (near.toPaddy[j * m.w + i]! <= 1) {
        // The earthen dikes between rice paddies.
        base = dikePixel(x, y, sunOn(view, gx, gy));
      } else {
        // Countryside: clumps of lush meadow and dry grass over bare earth on gentle swells,
        // turning to a sandy beach along the sea.
        const shore =
          blended(m, near.toWater, i, j, fx, fy) - 0.5 + (valueNoise(x / 9 + 2, y / 4.5) - 0.5) * 0.8;
        base = countryPixel(fv, x, y, gx, gy, shore);
      }
      [r, gg, bb] = base;
      if (ld < LAIR_RADIUS) {
        const t = 1 - ld / LAIR_RADIUS;
        r += 38 * t;
        gg -= 14 * t;
        bb -= 10 * t;
      }
    }
    if (m.biome === 'snow' || m.surface?.[j * m.w + i] === Surface.Snow) {
      // Snowfield: the same texture, lifted to cool white.
      const l = (r + gg + bb) / 3;
      r = 168 + l * 0.42;
      gg = 176 + l * 0.42;
      bb = 196 + l * 0.38;
    }
    // Each school's land has its own cast: misty Wudang, ochre Shaolin, rose Bliss Valley…
    if (m.tint) {
      r *= m.tint[0];
      gg *= m.tint[1];
      bb *= m.tint[2];
    }
    s[k] = r;
    s[k + 1] = gg;
    s[k + 2] = bb;
    s[k + 3] = 255;
  };
  return {
    c,
    img: ctx2d(c).createImageData(CW * res, CH * res),
    X0,
    Y0,
    CW,
    CH,
    res,
    pass: 0,
    row: 0,
    shown: false,
    pixel,
    specks,
  };
}

/** Rows of a chunk painted between looks at the clock. */
const BAND = 4;
const SPOT = new Uint8ClampedArray(4);

/** Paints a chunk on until `until` (a `performance.now()` time), or until it reaches pass `upTo`. */
function paintSome(job: GroundJob, until: number, upTo: 1 | 2 = 2): void {
  const { CW, CH, X0, Y0, res } = job;
  // Rows and columns of the canvas; a canvas pixel at (x, y) shows ground point (X0 + x/res, …).
  const PW = CW * res;
  const PH = CH * res;
  while (job.pass < upTo && job.img) {
    const s = job.img.data;
    const y0 = job.row;
    const y1 = Math.min(PH, y0 + BAND);
    if (job.pass === 0) {
      // Coarse: the middle pixel of each 4×4 block, filled across the block.
      for (let bx = 0; bx < PW; bx += BAND) {
        job.pixel(X0 + Math.min(PW - 1, bx + 2) / res, Y0 + Math.min(PH - 1, y0 + 2) / res, SPOT, 0);
        for (let y = y0; y < y1; y++)
          for (let x = bx; x < Math.min(PW, bx + BAND); x++) s.set(SPOT, (y * PW + x) * 4);
      }
    } else
      for (let y = y0; y < y1; y++)
        for (let x = 0; x < PW; x++) job.pixel(X0 + x / res, Y0 + y / res, s, (y * PW + x) * 4);
    job.row = y1;
    if (job.row >= PH) {
      // Lane C0-TRUEPX-GAME: the finished ground's true-pixel clean-up (render/truepx/), on the thread that painted it,
      // a band at a time within the same budget, before it goes on the canvas.
      if (
        job.pass === 1 &&
        truePxScene() &&
        !(job.clean ??= new GroundClean(new Uint32Array(job.img.data.buffer), PW, PH)).step(until)
      )
        return;
      job.clean = undefined;
      const g = ctx2d(job.c);
      g.putImageData(job.img, 0, 0);
      touchCanvas(job.c);
      job.shown = true;
      job.row = 0;
      if (job.pass === 0) job.pass = 1;
      else {
        job.pass = 2;
        job.img = null;
        g.save();
        g.scale(res, res);
        for (const [x, y, w, h, col] of job.specks)
          if (x + w > X0 && x < X0 + CW && y + h > Y0 && y < Y0 + CH) box(g, x - X0, y - Y0, w, h, col);
        g.restore();
      }
    }
    if (performance.now() >= until) return;
  }
}

/** Paints pixels [X0, X0+CW) × [Y0, Y0+CH) of the whole ground into a canvas of their own, all at once. */
function paintGround(
  m: GameMap,
  o: IsoOrigin,
  view: ViewMapping,
  X0: number,
  Y0: number,
  CW: number,
  CH: number,
  specks: readonly Speck[],
  near: Shore,
): HTMLCanvasElement {
  const job = groundJob(m, o, view, X0, Y0, CW, CH, specks, near);
  // Straight to every pixel: the coarse pass is only for showing something early.
  job.pass = 1;
  paintSome(job, Infinity);
  return job.c;
}

/** What painting one map's ground needs, worked out once (in a worker or on the main thread). */
export interface GroundSource {
  m: GameMap;
  o: IsoOrigin;
  view: ViewMapping;
  specks: Speck[];
  near: Shore;
}
export function groundSource(m: GameMap, o: IsoOrigin, view: ViewMapping): GroundSource {
  return { m, o, view, specks: groundSpecks(m, o), near: shoreDistances(m) };
}

/** A chunk of ground painted in full at `res` (used by the ground workers, see groundWorkers.ts). */
export function paintChunk(src: GroundSource, X0: number, Y0: number, CW: number, CH: number, res: number) {
  const job = groundJob(src.m, src.o, src.view, X0, Y0, CW, CH, src.specks, src.near, res);
  job.pass = 1;
  paintSome(job, Infinity);
  return job.c;
}

/** How the map is turned when the camera is at quarter turn `rot` (as IsoCamera.turn and unturn). */
export function viewFor(n: number, rot: number): ViewMapping {
  switch (rot & 3) {
    case 1:
      return { turn: (x, y) => [n - y, x], unturn: (x, y) => [y, n - x] };
    case 2:
      return { turn: (x, y) => [n - x, n - y], unturn: (x, y) => [n - x, n - y] };
    case 3:
      return { turn: (x, y) => [y, n - x], unturn: (x, y) => [n - y, x] };
    default:
      return SAME;
  }
}

/** The whole ground of a (small) map painted at once, without its scattered specks: for previews. */
export function groundPreview(m: GameMap, view: ViewMapping = SAME): HTMLCanvasElement {
  const o = mapOrigin(m);
  const W = (m.w + m.h) * 16;
  const H = (m.w + m.h) * 8 + o.oy * 2;
  return paintGround(m, o, view, 0, 0, W, H, [], shoreDistances(m));
}

/** The sky behind the world: dusk light, a pale sun low over three misty ridges. */
export function paintSky(W: number, H: number): HTMLCanvasElement {
  const c = canvas(W, H);
  const g = ctx2d(c);
  const gr = g.createLinearGradient(0, 0, 0, H);
  gr.addColorStop(0, '#26304c');
  gr.addColorStop(0.45, '#7a6a84');
  gr.addColorStop(0.62, '#d8a888');
  gr.addColorStop(1, '#2a2430');
  g.fillStyle = gr;
  g.fillRect(0, 0, W, H);
  // A pale sun low over the ridges.
  const sun = g.createRadialGradient(W * 0.68, H * 0.5, 0, W * 0.68, H * 0.5, H * 0.25);
  sun.addColorStop(0, 'rgba(255,236,200,0.9)');
  sun.addColorStop(0.15, 'rgba(255,220,180,0.5)');
  sun.addColorStop(1, 'rgba(255,220,180,0)');
  g.fillStyle = sun;
  g.fillRect(0, 0, W, H);
  const ridges: [number, number, string, number][] = [
    [0.48, 0.16, '#6e6a88', 1],
    [0.58, 0.2, '#4a4660', 2],
    [0.7, 0.22, '#2c2a3a', 3],
  ];
  for (const [base, amp, col, seed] of ridges) {
    g.fillStyle = col;
    for (let x = 0; x < W; x++) {
      const n = valueNoise(x / 60 + seed * 13, seed) * 0.7 + valueNoise(x / 18 + seed * 7, seed + 3) * 0.3;
      const top = Math.round(H * (base - amp * n));
      g.fillRect(x, top, 1, H - top);
    }
    // Mist pooling at the foot of each ridge.
    const mist = g.createLinearGradient(0, H * base - H * 0.04, 0, H * base + H * 0.08);
    mist.addColorStop(0, 'rgba(230,220,230,0)');
    mist.addColorStop(1, 'rgba(230,220,230,0.18)');
    g.fillStyle = mist;
    g.fillRect(0, H * base - H * 0.04, W, H * 0.12);
  }
  return c;
}

/** Per tile: how far (in tiles, capped) to the sea, to a rice paddy, and from the sea to land. */
export interface Shore {
  toWater: Uint8Array;
  toPaddy: Uint8Array;
  toLand: Uint8Array;
  /** On a map with a surface layer: tiles in from the edge of a road (0 off it). */
  toVerge: Uint8Array;
}

/** Breadth-first distances from every tile of one kind, out to a few tiles. */
function distanceFrom(m: GameMap, from: (t: number, n: number) => boolean, cap: number): Uint8Array {
  const d = new Uint8Array(m.w * m.h).fill(cap + 1);
  let edge: number[] = [];
  for (let n = 0; n < m.w * m.h; n++)
    if (from(m.tiles[n]!, n)) {
      d[n] = 0;
      edge.push(n);
    }
  for (let step = 1; step <= cap && edge.length; step++) {
    const next: number[] = [];
    for (const n of edge) {
      const i = n % m.w;
      const j = (n / m.w) | 0;
      for (const [a, b] of [
        [i + 1, j],
        [i - 1, j],
        [i, j + 1],
        [i, j - 1],
      ] as const) {
        if (a < 0 || b < 0 || a >= m.w || b >= m.h) continue;
        const q = b * m.w + a;
        if (d[q]! > step) {
          d[q] = step;
          next.push(q);
        }
      }
    }
    edge = next;
  }
  return d;
}

function shoreDistances(m: GameMap): Shore {
  return {
    toWater: distanceFrom(m, (t) => t === Tile.Water, 4),
    toPaddy: distanceFrom(m, (t) => t === Tile.Paddy, 2),
    toLand: distanceFrom(m, (t) => t !== Tile.Water, 8),
    // Roads of an authored layout: how far in from their verge (the ruts run down the middle).
    toVerge: m.surface
      ? distanceFrom(m, (_t, n) => !onRoad(m, (n % m.w) + 0.5, Math.floor(n / m.w) + 0.5), 3)
      : new Uint8Array(0),
  };
}

/**
 * The sea (deep blue out, turquoise over the shallows, foam where it laps the shore), rice paddies
 * (flooded plots with rows of green shoots) and the pier's planks; null for every other ground.
 */
function waterLook(
  m: GameMap,
  near: Shore,
  t: number,
  i: number,
  j: number,
  fx: number,
  fy: number,
  x: number,
  y: number,
): RGB | null {
  if (t === Tile.Water) {
    // Tiles out to sea, blended between tile centres so the depth bands run smooth.
    const reach = blended(m, near.toLand, i, j, fx, fy) - 1 + (valueNoise(x / 8 + 5, y / 4) - 0.5) * 0.6;
    // Out from the beach: foam, a line of surf, then the shallows deepening to blue.
    return seaPixel(x, y, Math.max(0, reach + 0.5));
  }
  if (t === Tile.Paddy) return paddyPixel(x, y, fx);
  if (t === Tile.Pier) {
    // Planks laid across the walk, dark seams between them, the odd knot.
    const plank = (fy * 5) % 1;
    let mul = plank < 0.14 ? 0.68 : 1;
    if (fx < 0.05 || fx > 0.95) mul *= 0.85;
    if (hash2(x, y) > 0.985) mul *= 0.7;
    mul += (hash2(x * 3, y) - 0.5) * 0.08;
    return [146 * mul, 108 * mul, 70 * mul];
  }
  return null;
}

/** Tufts, pebbles, cracks and the bones round the lair, scattered over the whole ground. */
function groundSpecks(m: GameMap, o: IsoOrigin): Speck[] {
  const out: Speck[] = [];
  const put = (x: number, y: number, w: number, h: number, col: string) => out.push([x, y, w, h, col]);
  const dr = mulberry32(m.seed * 13 + 4);
  // As many per tile as on the first 72×72 map.
  const count = Math.round((1400 * m.w * m.h) / (72 * 72));
  for (let n = 0; n < count; n++) {
    const i = 2 + Math.floor(dr() * (m.w - 4));
    const j = 2 + Math.floor(dr() * (m.h - 4));
    if (tileAt(m, i, j) !== Tile.Floor || nearPlaza(m, i + 0.5, j + 0.5, PLAZA_RADIUS)) continue;
    const [sx, sy] = toScreen(i + 0.2 + dr() * 0.6, j + 0.2 + dr() * 0.6, o);
    const x = Math.round(sx);
    const y = Math.round(sy);
    const t = dr();
    if (nearLair(m, i + 0.5, j + 0.5, 5)) {
      if (t < 0.5) {
        put(x, y, 2, 1, '#e8e0d0');
        put(x + 2, y - 1, 1, 1, '#e8e0d0');
      } else put(x, y, 3, 1, '#5a2622');
      continue;
    }
    if (t < 0.45) {
      put(x, y, 1, 2, '#5d7448');
      put(x + 1, y - 1, 1, 3, '#7c9a5c');
      put(x + 2, y, 1, 2, '#5d7448');
    } else if (t < 0.75) {
      put(x, y, 2, 1, '#a59c8e');
      put(x, y + 1, 2, 1, '#4a433c');
    } else {
      let cx = x;
      let cy = y;
      for (let q = 0; q < 5; q++) {
        put(cx, cy, 1, 1, 'rgba(50,44,38,.7)');
        cx += dr() < 0.5 ? 1 : -1;
        cy += dr() < 0.6 ? 0 : 1;
      }
    }
  }
  return out;
}

/** Static scenery that must be depth-sorted together with entities. */
/** Maps view coordinates back to the map (and forward), so decoration stays put when the camera turns. */
export interface ViewMapping {
  turn(x: number, y: number): [number, number];
  unturn(x: number, y: number): [number, number];
}
const SAME: ViewMapping = { turn: (x, y) => [x, y], unturn: (x, y) => [x, y] };

/** Grassy (> 0.6) or bare-dirt (< 0.3) patches of the countryside, in map coordinates. */
export const biomeAt = (x: number, y: number): number => valueNoise(x / 5.5 + 40, y / 5.5 + 17);

/**
 * Tall grass, waist-high patches in the lush meadows (`grass.ts` draws and sways them): open tiles
 * clear of the plaza, roads, the lair and the water. Returns how thick the patch is there, 0 = none.
 */
export function tallGrassAt(m: GameMap, view: ViewMapping, i: number, j: number): number {
  // The classic plains (classicPlains.ts) had short tufts only.
  if (m.biome === 'snow' || classicPlains(m) || tileAt(m, i, j) !== Tile.Floor || isBorder(m, i, j)) return 0;
  const [x, y] = [i + 0.5, j + 0.5];
  if (nearPlaza(m, x, y, PLAZA_RADIUS + 1.5) || nearLair(m, x, y, LAIR_RADIUS + 2)) return 0;
  for (let b = j - 1; b <= j + 1; b++)
    for (let a = i - 1; a <= i + 1; a++) {
      const t = tileAt(m, a, b);
      if (t === Tile.Water || t === Tile.Paddy || t === Tile.Pier || onRoad(m, a + 0.5, b + 0.5)) return 0;
    }
  const [ux, uy] = view.unturn(x, y);
  if (biomeAt(ux, uy) < 0.45) return 0;
  return Math.min(1, Math.max(0, (valueNoise(ux / 4.5 + 71, uy / 4.5 + 29) - 0.6) / 0.1));
}

/** Under trees: a tree on one of the eight neighbouring tiles. */
const shaded = (m: GameMap, i: number, j: number): boolean => {
  for (let b = j - 1; b <= j + 1; b++)
    for (let a = i - 1; a <= i + 1; a++) if (tileAt(m, a, b) === Tile.Tree) return true;
  return false;
};

/** Cloth and emblem of the school banners round the plaza. */
export const SCHOOL_FLAGS: readonly (readonly [cloth: string, mark: string])[] = [
  ['#262a36', '#eef0f3'],
  ['#9a3424', '#f0c040'],
  ['#8a1c22', '#e8b84a'],
  ['#3a1a4a', '#e86aa8'],
  ['#3a6a9a', '#ffffff'],
  ['#5a0a10', '#e8303a'],
];

/** One rock block per variant (the index `GameMap.variant` holds): boulders, cliffs, mountainside. */
export function rockSet(): HTMLCanvasElement[] {
  return [
    rock(12, 1, false),
    rock(15, 2, false),
    rock(10, 3, false),
    rock(17, 4, false),
    rock(24, 5, true),
    rock(28, 6, true),
    rock(40, 7, true, 'forest'),
    rock(54, 8, true, 'forest'),
    rock(70, 9, true, 'forest'),
    rock(88, 10, true, 'forest'),
    rock(108, 11, true, 'crag'),
    rock(130, 12, true, 'snow'),
  ];
}

/** Width of the vertical strips buildings are cut into for depth sorting. */
const STRIP = 8;

/** The pictures the scenery is drawn with: made once per camera angle and shared by every part of the map. */
export interface PropKit {
  readonly snow: boolean;
  readonly classic: boolean;
  readonly rocks: HTMLCanvasElement[];
  readonly trees: ReturnType<typeof treeSprites>;
  readonly deadImg: HTMLCanvasElement;
  readonly bushes: HTMLCanvasElement[];
  readonly blooms: HTMLCanvasElement[];
  readonly grasses: HTMLCanvasElement[];
  readonly lantern: HTMLCanvasElement;
  readonly steleImg: HTMLCanvasElement;
  readonly swordImg: HTMLCanvasElement;
  readonly posts: HTMLCanvasElement;
  readonly reedImgs: HTMLCanvasElement[];
  readonly pads: HTMLCanvasElement[];
  readonly ferns: HTMLCanvasElement[];
  readonly caps: HTMLCanvasElement[];
  readonly pebbles: HTMLCanvasElement[];
  readonly banners: HTMLCanvasElement[];
  /** The town's low earth wall, one tile of it (the 256 m plains' hub wall). */
  readonly earthWall: HTMLCanvasElement;
  readonly shore: Shore;
  /** Building pictures by look, and each one cut in strips (shared by every building drawn alike). */
  readonly looks: Map<string, Sprite>;
  readonly strips: Map<HTMLCanvasElement, HTMLCanvasElement[]>;
  /** Building strips (left as drawn by the finishing pass). */
  readonly built: Set<HTMLCanvasElement>;
  readonly detail: Map<HTMLCanvasElement, Detail>;
}

/** Building pictures and their strips shared by the kits of every camera angle (`propKit(m, true)`). */
const SHARED = { looks: new Map<string, Sprite>(), strips: new Map<HTMLCanvasElement, HTMLCanvasElement[]>(), built: new Set<HTMLCanvasElement>() };

/** Makes the scenery's pictures for a map (one camera angle); `share`: building pictures kept across angles. */
export function propKit(m: GameMap, share = false): PropKit {
  const rocks = rockSet();
  const snow = m.biome === 'snow';
  const classic = classicPlains(m);
  const trees = snow ? snowTreeSprites() : treeSprites();
  if (snow) for (let k = 0; k < rocks.length; k++) rocks[k] = snowify(rocks[k]!);
  const deadImg = deadTree();
  const bushes = [bush(1), bush(2), bush(3)];
  const blooms = [flowers(0), flowers(1), flowers(2), flowers(3)];
  const grasses = [tallGrass(5), tallGrass(6)];
  const lantern = stoneLantern();
  const steleImg = stele();
  const swordImg = groundSword();
  const posts = pierPost();
  const reedImgs = [reeds(1), reeds(2), reeds(3)];
  const pads = [lilyPads(0), lilyPads(1), lilyPads(2)];
  const ferns = [fern(1), fern(2)];
  const caps = [mushrooms(0), mushrooms(1)];
  const pebbles = [stones(1), stones(2), stones(3)];
  const shore = shoreDistances(m);
  const banners = SCHOOL_FLAGS.map(([cloth, mark]) => banner(cloth, mark));
  const earth = earthWall();
  // Nature finished with its detail pass and rim of light (see `finishArt`), on the same grid as
  // the buildings, which are left as drawn.
  const detail = new Map<HTMLCanvasElement, Detail>();
  for (const list of Object.values(trees)) for (const t of list) detail.set(t.img, 'plant');
  for (const img of [deadImg, ...bushes]) detail.set(img, 'plant');
  for (const img of [...rocks, ...pebbles, steleImg]) detail.set(img, 'stone');
  return {
    snow,
    classic,
    rocks,
    trees,
    deadImg,
    bushes,
    blooms,
    grasses,
    lantern,
    steleImg,
    swordImg,
    posts,
    reedImgs,
    pads,
    ferns,
    caps,
    pebbles,
    banners,
    earthWall: earth,
    shore,
    ...(share ? SHARED : { looks: new Map(), strips: new Map(), built: new Set() }),
    detail,
  };
}

/** A building's picture (a structure as `structureLook` draws it), in the master look. */
export function buildingLook(kit: PropKit, st: Structure): Sprite {
  // C0-ROTATE-PORT: a picture painted now notes its faces (buildings.ts `faceRec`) for the turned camera
  // (render/turned/kinds.ts `paintedFaces`); one taken from the cache already has them.
  const own = !faceRec.on;
  if (own) {
    faceRec.on = true;
    faceRec.faces = [];
  }
  try {
    const raw = structureSprite(st, kit.looks);
    // Lane C0-TRUEPX-GAME: then the true-pixel clean-up (render/truepx/), when on.
    const sp = truePxSprite(masterLookSprite(raw));
    if (own) {
      const faces = faceRec.faces.length ? faceRec.faces : paintedFaces.get(raw.img);
      if (faces) {
        paintedFaces.set(raw.img, faces);
        paintedFaces.set(sp.img, faces);
      }
    }
    return sp;
  } finally {
    if (own) {
      faceRec.on = false;
      faceRec.faces = [];
    }
  }
}

/** A building picture cut in strips, once per picture and shared by the buildings drawn alike. */
export function buildingStrips(kit: PropKit, sp: Sprite): HTMLCanvasElement[] {
  let strips = kit.strips.get(sp.img);
  if (!strips) {
    strips = [];
    for (let px = 0; px < sp.img.width; px += STRIP) {
      const sw = Math.min(STRIP, sp.img.width - px);
      const strip = canvas(sw, sp.img.height);
      ctx2d(strip).drawImage(sp.img, px, 0, sw, sp.img.height, 0, 0, sw, sp.img.height);
      strips.push(strip);
      kit.built.add(strip);
    }
    kit.strips.set(sp.img, strips);
  }
  return strips;
}

/** How tall the town's earth wall stands (art px): under 40 px, below a figure's shoulders. */
const EARTH_WALL_H = 14;

/**
 * One tile of the town's low wall: rammed earth in courses under a cap of dark tiles, lit from the upper right
 * (the right face the lighter), dark outline. Tiles in a row read as one wall.
 */
function earthWall(): HTMLCanvasElement {
  const H = EARTH_WALL_H;
  const c = canvas(34, H + 18);
  const g = ctx2d(c);
  const P = (x: number, y: number): [number, number] => [x + 1, y + 1];
  const quad = (pts: [number, number][], col: string) => {
    g.fillStyle = col;
    g.beginPath();
    pts.forEach(([x, y], k) => (k ? g.lineTo(x, y) : g.moveTo(x, y)));
    g.closePath();
    g.fill();
  };
  // Faces: left (in shade), right (lit).
  quad([P(0, 8), P(16, 16), P(16, 16 + H), P(0, 8 + H)], '#8a6a46');
  quad([P(16, 16), P(32, 8), P(32, 8 + H), P(16, 16 + H)], '#b48c5c');
  // Courses of rammed earth every 4 px.
  for (let y = 4; y < H; y += 4) {
    for (let x = 0; x < 16; x++) box(g, 1 + x, 1 + 8 + y + Math.floor(x / 2), 1, 1, '#76583a');
    for (let x = 16; x < 32; x++) box(g, 1 + x, 1 + 16 + y - Math.floor((x - 16) / 2), 1, 1, '#9a7448');
  }
  // The cap of dark tiles, a little proud, with a lit ridge.
  quad([P(16, -2), P(33, 7), P(16, 16), P(-1, 7)], '#4a4642');
  quad([P(16, 0), P(31, 7.5), P(16, 15), P(1, 7.5)], '#5e5952');
  for (let x = 2; x < 31; x += 3) box(g, 1 + x, 1 + 7, 1, 1, '#7a746a');
  g.imageSmoothingEnabled = false;
  return outline(crispAlpha(c), '#28221d');
}

/** Alpha snapped to on or off (hard pixel edges for the filled faces). */
function crispAlpha(c: HTMLCanvasElement): HTMLCanvasElement {
  const g = ctx2d(c);
  const img = g.getImageData(0, 0, c.width, c.height);
  for (let k = 3; k < img.data.length; k += 4) img.data[k] = img.data[k]! >= 110 ? 255 : 0;
  g.putImageData(img, 0, 0);
  return c;
}

/** Tile rectangle [i0, i1) × [j0, j1). */
export type TileRect = readonly [i0: number, i1: number, j0: number, j1: number];

/**
 * The scenery of one part of the map (tiles `area`, the `structures` given, and the plaza's lanterns, banners and
 * swords standing where `has` says), as drawn, before the finishing pass (`finishProps`). Over the whole map it is
 * the whole scenery, in the order `buildProps` has always made it.
 */
export function propsIn(
  kit: PropKit,
  m: GameMap,
  o: IsoOrigin,
  view: ViewMapping,
  area: TileRect,
  structures: readonly Structure[],
  has: (x: number, y: number) => boolean,
): Prop[] {
  const { rocks, trees, deadImg, bushes, blooms, grasses, lantern, steleImg, swordImg, posts, reedImgs, pads } = kit;
  const { ferns, caps, pebbles, shore, snow, classic } = kit;
  const heights = ROCK_HEIGHTS;
  const props: Prop[] = [];
  const [i0, i1, j0, j1] = [Math.max(0, area[0]), Math.min(m.w, area[1]), Math.max(0, area[2]), Math.min(m.h, area[3])];
  for (let j = j0; j < j1; j++)
    for (let i = i0; i < i1; i++) {
      const t = tileAt(m, i, j);
      if (t === Tile.Floor) continue;
      const [px0, py0] = toScreen(i + 0.5, j + 0.5, o);
      if (t === Tile.Water) {
        // Reeds standing in the shallows, lotus pads a little further out.
        if (snow) continue;
        const land = shore.toLand[j * m.w + i]!;
        const [ui, uj] = view.unturn(i + 0.5, j + 0.5);
        const hv = hash2(Math.floor(ui) * 7 + 13, Math.floor(uj) * 11 + 5);
        const img =
          land === 1 && hv < 0.3
            ? reedImgs[Math.floor(hv * 100) % 3]!
            : land >= 2 && land <= 4 && hv < 0.07
              ? pads[Math.floor(hv * 1000) % 3]!
              : null;
        if (img)
          props.push({
            img,
            x: Math.round(px0 - img.width / 2 + (hv - 0.15) * 20),
            y: Math.round(py0 - img.height + (pads.includes(img) ? 7 : 4)),
            d: i + j + (img.height > 20 ? 0.6 : -0.5),
          });
        continue;
      }
      // Rice in the paddies sways with the tall grass (grass.ts).
      if (t === Tile.Paddy) continue;
      if (t === Tile.Pier) {
        // Posts along the pier's edges where it meets the water.
        const edge = [
          [1, 0],
          [0, 1],
        ].some(([a, b]) => tileAt(m, i + a!, j + b!) === Tile.Water);
        if (edge && (i + j) % 2 === 0)
          props.push({ img: posts, x: Math.round(px0 - 3), y: Math.round(py0 - 6), d: i + j + 0.9 });
        continue;
      }
      const [sx, sy] = toScreen(i, j, o);
      if (t === Tile.Rock) {
        let v = m.variant[j * m.w + i]!;
        if (isBorder(m, i, j) && v < 4) v = 4;
        props.push({
          img: rocks[v]!,
          x: sx - 16,
          y: sy - heights[v]!,
          d: i + j + 1,
          ...(v < 6 ? { ao: true as const } : {}),
          ...(turnedBlock(m, t) ? { turned: { kind: 'block' as const, i, j, h: heights[v]! } } : {}),
        });
        // Pines standing on the forested mountainsides.
        if (v >= 6 && v <= 9 && !snow) {
          const [ui, uj] = view.unturn(i + 0.5, j + 0.5);
          const hv = hash2(Math.floor(ui) * 11 + 5, Math.floor(uj) * 13 + 2);
          if (hv < 0.3) {
            const set = trees.pine;
            const tr = set[Math.floor(hv * 1000) % set.length]!;
            props.push({ img: tr.img, x: sx - tr.ax, y: sy + 8 - tr.ay - heights[v]!, d: i + j + 1.05 });
          }
        }
      } else if (t === Tile.Tree) {
        // The species and variant come from the tile's place on the map (not on screen); a map that names
        // the species in the tile's variant (the 256 m plains, plainsMap.ts) has it as laid.
        const [ui, uj] = view.unturn(i + 0.5, j + 0.5);
        const hv = hash2(Math.floor(ui) * 3 + 7, Math.floor(uj) * 5 + 1);
        const named = classic ? TREE_SPECIES[m.variant[j * m.w + i]!] : undefined;
        if (nearLair(m, i + 0.5, j + 0.5, LAIR_RADIUS + 3))
          props.push({ img: deadImg, x: sx - 20, y: sy + 8 - 68, d: i + j + 1 });
        else {
          const set =
            trees[
              named && named in trees
                ? (named as keyof typeof trees)
                : shore.toWater[j * m.w + i]! <= 4 && hv < 0.8
                  ? 'willow'
                  : treeKindAt(hv)
            ];
          const t: Sprite = set[Math.floor(hash2(Math.floor(ui) + 17, Math.floor(uj) * 3) * set.length)]!;
          props.push({
            img: t.img,
            x: sx - t.ax,
            y: sy + 8 - t.ay,
            d: i + j + 1,
            cast: { kind: 'tree', x: sx, y: sy + 8, h: t.ay, half: Math.round(t.img.width * 0.18) },
          });
        }
      } else if (t === Tile.Stele) props.push({ img: steleImg, x: sx - 13, y: sy + 8 - 46, d: i + j + 1 });
      else if (t === Tile.Wall) {
        // A wall of an authored layout (a terrace's retaining wall, the arena's ring, a fence, a
        // post of the pole forest): drawn as a block of stone until it has art of its own.
        const wv = m.variant[j * m.w + i]!;
        if (classic && wv === HUB_WALL_VARIANT) {
          // The town's low earth wall (the 256 m plains' hub edge).
          props.push({
            img: kit.earthWall,
            x: sx - 17,
            y: sy - EARTH_WALL_H - 1,
            d: i + j + 1,
            ao: true,
            ...(turnedBlock(m, t) ? { turned: { kind: 'block' as const, i, j, h: EARTH_WALL_H } } : {}),
          });
          continue;
        }
        const v = [4, 2, 1][wv] ?? 4;
        props.push({
          img: rocks[v]!,
          x: sx - 16,
          y: sy - heights[v]!,
          d: i + j + 1,
          ao: true,
          ...(turnedBlock(m, t) ? { turned: { kind: 'block' as const, i, j, h: heights[v]! } } : {}),
        });
      }
    }
  // Buildings, cut into vertical strips each sorted at the depth of the footprint's front edge
  // under it, so fighters walking round a building pass in front of or behind the right part.
  for (const raw of structures) {
    // An authored layout's kinds are drawn as the nearest existing sprite (layoutLook.ts).
    const st = structureLook(raw);
    if (!st) continue;
    const sp = buildingLook(kit, st);
    const [sx, sy] = toScreen(st.i, st.j, o);
    const x0 = Math.round(sx - sp.ax);
    const y0 = Math.round(sy - sp.ay);
    // Its sun shadow from the front-left wall: about three quarters of the way to the ridge.
    const [wx, wy] = toScreen(st.i, st.j + st.h, o);
    const height = Math.round((sp.ay + (st.w + st.h) * 4) * 0.75);
    let cast: Caster | undefined = CASTS.has(st.kind)
      ? { kind: 'block', x: wx, y: wy, w: st.w, h: height }
      : undefined;
    const foot = toScreen(st.i + st.w / 2, st.j + st.h / 2, o);
    // C0-ROTATE-PORT: the buildings whose painters noted their faces turn with the camera (the kit's all do).
    const kitted = kitDraws(raw);
    const turned: TurnedSource | undefined =
      (kitted || TURNED_KINDS.has(st.kind)) && paintedFaces.has(sp.img)
        ? {
            kind: 'building',
            st,
            img: sp.img,
            ax: sp.ax,
            ay: sp.ay,
            swap: !kitted && (st.kind === 'gate' || st.kind === 'moongate') && st.axis === 'y',
          }
        : undefined;
    buildingStrips(kit, sp).forEach((strip, k) => {
      const px = k * STRIP;
      const sw = strip.width;
      const u = Math.max(-st.h, Math.min(st.w, (px + sw / 2 - sp.ax) / 16));
      const front = 2 * Math.min(st.w, u + st.h) - u;
      props.push({ img: strip, x: x0 + px, y: y0, d: st.i + st.j + front, cast, foot, ...(turned ? { turned } : {}) });
      cast = undefined;
    });
  }
  // Ground cover on open tiles: bushes, flowers and tall grass, thicker in grassy patches.
  for (let j = j0; j < j1; j++)
    for (let i = i0; i < i1; i++) {
      if (tileAt(m, i, j) !== Tile.Floor || isBorder(m, i, j)) continue;
      if (nearPlaza(m, i + 0.5, j + 0.5, PLAZA_RADIUS + 0.5) || onRoad(m, i + 0.5, j + 0.5)) continue;
      if (tallGrassAt(m, view, i, j) > 0) continue; // the tall grass grows there instead
      const [ui, uj] = view.unturn(i + 0.5, j + 0.5);
      const h1 = hash2(Math.floor(ui) * 13 + 3, Math.floor(uj) * 7 + 11);
      const grassy = biomeAt(ui, uj) > 0.58;
      const lair = nearLair(m, i + 0.5, j + 0.5, LAIR_RADIUS + 1);
      const ox = (hash2(Math.floor(ui) + 91, Math.floor(uj)) - 0.5) * 12;
      const [sx, sy] = toScreen(i + 0.5, j + 0.5, o);
      let img: HTMLCanvasElement | null = null;
      if (snow) img = null;
      else if (lair) img = h1 < 0.08 ? deadImg : null;
      else if (shore.toWater[j * m.w + i]! === 1 && h1 < 0.5) img = reedImgs[Math.floor(h1 * 100) % 3]!;
      else if (h1 < 0.035) img = bushes[Math.floor(h1 * 1000) % 3]!;
      else if (h1 < (grassy ? 0.12 : 0.06)) img = blooms[Math.floor(h1 * 1000) % 4]!;
      else if (h1 < (grassy ? 0.3 : 0.1)) img = grasses[Math.floor(h1 * 1000) % 2]!;
      else if (classic) img = null;
      else if (h1 < (grassy ? 0.34 : 0.13)) img = pebbles[Math.floor(h1 * 1000) % 3]!;
      else if (h1 < 0.37 && shaded(m, i, j))
        img = h1 < 0.355 ? ferns[Math.floor(h1 * 1000) % 2]! : caps[Math.floor(h1 * 1000) % 2]!;
      if (img)
        props.push({
          img,
          x: Math.round(sx + ox - img.width / 2),
          y: Math.round(sy - img.height + 3),
          d: i + j + 0.6,
        });
    }
  // Stone lanterns ringing the plaza and banners of the five schools at its edge.
  const place = (gx: number, gy: number, img: HTMLCanvasElement, foot: number) => {
    const [vx, vy] = view.turn(gx, gy);
    if (!has(vx, vy)) return;
    const [sx, sy] = toScreen(vx, vy, o);
    props.push({ img, x: Math.round(sx - img.width / 2), y: Math.round(sy - img.height + foot), d: vx + vy });
  };
  const P0 = view.unturn(m.plaza.x, m.plaza.y);
  // The 256 m plains' plaza has the kit's lanterns on its rim and sword stones round the stele (data/layouts/plains.ts
  // PLAINS_PROPS, drawn by passes/kitProps.ts) in place of these.
  const village = m.kind === 'village' || (classic && m.w === 256);
  if (!(classic && m.w === 256)) for (const [x, y] of plazaLanternRing(P0[0], P0[1])) place(x, y, lantern, 2);
  const flags = SCHOOL_FLAGS;
  if (!village)
    flags.forEach((_, k) => {
      const a = (k / flags.length) * Math.PI * 2 - Math.PI / 2 + 0.3;
      place(P0[0] + Math.cos(a) * 6.6, P0[1] + Math.sin(a) * 6.6, kit.banners[k]!, 2);
    });
  for (let k = 0; k < (village ? 0 : 9); k++) {
    const a = (k / 9) * Math.PI * 2 - 0.4;
    const gx = m.plaza.x + Math.cos(a) * 2.4;
    const gy = m.plaza.y + Math.sin(a) * 2.4;
    if (!has(gx, gy)) continue;
    const [sx, sy] = toScreen(gx, gy, o);
    props.push({ img: swordImg, x: Math.round(sx - 4), y: Math.round(sy - 20), d: gx + gy });
  }
  return props;
}

/** The finishing pass over scenery made by `propsIn` (in place): detail and rim on nature, the master look. */
export function finishProps(kit: PropKit, props: Prop[]): Prop[] {
  // The classic plains' props stay as drawn (classicPlains.ts).
  if (!kit.classic) for (const p of props) if (!kit.built.has(p.img)) p.img = finishArt(p.img, true, kit.detail.get(p.img));
  for (const p of props) if (!kit.built.has(p.img)) p.img = truePxImg(masterLookImg(p.img));
  return props;
}

export function buildProps(m: GameMap, o: IsoOrigin, view: ViewMapping = SAME): Prop[] {
  const kit = propKit(m);
  return finishProps(kit, propsIn(kit, m, o, view, [0, m.w, 0, m.h], m.structures, () => true));
}

/** 2px-per-tile top-down minimap; the DOM rotates it into the isometric diamond. */
export function buildMinimap(m: GameMap): HTMLCanvasElement {
  const c = canvas(m.w * 2, m.h * 2);
  const g = ctx2d(c);
  for (let j = 0; j < m.h; j++)
    for (let i = 0; i < m.w; i++) {
      const t = tileAt(m, i, j);
      const x = i + 0.5;
      const y = j + 0.5;
      const col =
        t === Tile.Water
          ? '#2f5a86'
          : t === Tile.Paddy
            ? '#5f8a5a'
            : t === Tile.Pier
              ? '#8a6a44'
              : t === Tile.Paved || t === Tile.Terrace
                ? '#a49c8c'
                : t === Tile.Rock
                  ? '#2e2a26'
                  : t === Tile.Tree
                    ? '#2f5a40'
                    : t === Tile.Building
                      ? '#a3473a'
                      : t === Tile.Stele
                        ? '#d9b46c'
                        : nearPlaza(m, x, y, PLAZA_RADIUS)
                          ? '#9a8f7c'
                          : onRoad(m, x, y)
                            ? '#7d6a55'
                            : nearLair(m, x, y, LAIR_RADIUS)
                              ? '#7a3a34'
                              : '#6a6258';
      box(g, i * 2, j * 2, 2, 2, col);
    }
  return c;
}
